import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Edit3, Trash2, Sparkles, RefreshCw, ExternalLink, ShieldAlert, Plus, MailX } from "lucide-react";

/**
 * /ad/marketing/coaches — Admin-only coach moderation console.
 *
 * Search + filter every coach_profile in the TKG. Edit any field inline,
 * soft-delete with one click (or hard-delete on confirm), or paste a bio URL
 * to re-enrich a profile through Claude.
 *
 * Gated by Protected role="athletic_director"; route is only useful to a
 * platform admin (non-admin AD sees an empty list because the backend
 * requires require_platform_admin).
 */
export default function AdminCoaches() {
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState(() => ({
    sport: "Lacrosse", gender: "", division: "",
    claimed: new URLSearchParams(window.location.search).get("claimed") || "",
  }));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [hardConfirm, setHardConfirm] = useState(false);
  useEffect(() => { setHardConfirm(false); }, [editing]);
  useEffect(() => {
    if (!hardConfirm) return;
    const t = setTimeout(() => setHardConfirm(false), 5000);
    return () => clearTimeout(t);
  }, [hardConfirm]);
  const [enriching, setEnriching] = useState(null);
  const [enrichUrl, setEnrichUrl] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      params.set("limit", "100");
      const { data } = await api.get(`/admin/coaches?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load coaches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const body = {
        name: editing.name,
        headline: editing.headline,
        current_organization: editing.current_organization,
        current_role: editing.current_role,
        current_division: editing.current_division,
        email: editing.email,
        phone: editing.phone,
      };
      const { data } = await api.patch(`/admin/coaches/${editing.id}`, body);
      setItems((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      toast.success("Coach updated");
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const toggleOptOut = async (c) => {
    const undo = !!c.email_unsubscribed;
    if (!window.confirm(undo
      ? `Re-subscribe ${c.name} (${c.email}) to CoachConnect emails?`
      : `Opt ${c.name} (${c.email}) out of ALL CoachConnect emails? Use this when someone replies STOP.`)) return;
    try {
      await api.post(undo ? "/admin/optout/undo" : "/admin/optout", { email: c.email });
      setItems((prev) => prev.map((x) => x.id === c.id ? { ...x, email_unsubscribed: !undo } : x));
      toast.success(undo ? `${c.name} re-subscribed` : `${c.name} opted out — no more emails, ever`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opt-out failed");
    }
  };

  const softDelete = async (c) => {
    if (!window.confirm(`Soft-delete ${c.name}? The profile will be hidden from talent pool + search but kept for restoration.`)) return;
    try {
      await api.delete(`/admin/coaches/${c.id}`);
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      setEditing(null);
      toast.success(`${c.name} removed from public view`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const hardDelete = async (c) => {
    if (!hardConfirm) { setHardConfirm(true); return; }
    setHardConfirm(false);
    try {
      await api.delete(`/admin/coaches/${c.id}?hard=true`);
      setItems((prev) => prev.filter((x) => x.id !== c.id));
      setEditing(null);
      // Also reload the list from server to ensure any related sibling
      // duplicates (same name/org) that we didn't hard-delete are surfaced.
      await load();
      toast.success(`${c.name} permanently deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hard delete failed");
    }
  };

  const enrich = async (c) => {
    if (!enrichUrl.startsWith("http")) {
      toast.error("Paste a full bio URL (https://…)");
      return;
    }
    setEnriching(c.id);
    try {
      const { data } = await api.post(`/admin/coaches/${c.id}/enrich`, { url: enrichUrl });
      toast.success(`${c.name} re-scored to ${data.score} (${data.tier})`);
      setEnrichUrl("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrich failed");
    } finally {
      setEnriching(null);
    }
  };

  return (
    <div className="text-white">
      <div className="max-w-7xl px-6 lg:px-8 py-8" data-testid="admin-coaches-page">
        <div className="flex items-center gap-2 text-xs tracking-widest text-volt heading">
          <ShieldAlert size={14} /> PLATFORM ADMIN · COACH MODERATION
        </div>
        <h1 className="heading text-3xl md:text-4xl mt-2">TKG · Coach Console</h1>
        <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
          Search every coach profile in the platform. Edit metadata, soft-delete bad scrapes, or paste an athletic-department bio URL to re-enrich a record through Claude.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/hq/jobs/new"
            data-testid="admin-create-job-btn"
            className="inline-flex items-center gap-2 bg-blaze text-white heading text-xs tracking-widest px-4 py-2 hover:glow-blaze transition-all"
          >
            <Plus size={14} /> CREATE NEW JOB (ADMIN)
          </Link>
        </div>

        {/* Search + filter bar */}
        <div className="mt-6 border border-neutral-800 p-4 bg-[#080A0F]">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[260px]">
              <label className="text-[10px] tracking-widest text-neutral-500">SEARCH</label>
              <div className="flex items-center gap-2 mt-1">
                <Search size={14} className="text-neutral-500" />
                <input
                  data-testid="admin-coach-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="Search name, organization, email"
                  className="flex-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
                />
              </div>
            </div>
            <Select label="GENDER" value={filters.gender} options={["", "Men", "Women"]} onChange={(v) => setFilters({ ...filters, gender: v })} />
            <Select label="DIVISION" value={filters.division} options={["", "D1", "D2", "D3", "NAIA", "NJCAA", "MCLA", "WCLA", "HS"]} onChange={(v) => setFilters({ ...filters, division: v })} />
            <SelectLabeled label="STATUS" value={filters.claim_state} onChange={(v) => setFilters({ ...filters, claim_state: v })}
              options={[
                { v: "", l: "all" },
                { v: "claimed", l: "any claim activity" },
                { v: "auto", l: "claim link clicked" },
                { v: "account", l: "account created" },
                { v: "unclaimed", l: "unclaimed profile" },
                { v: "declined", l: "declined / opted out" },
              ]} testid="filter-claimed" />
            <SelectLabeled label="SORT" value={filters.sort} onChange={(v) => setFilters({ ...filters, sort: v })}
              options={[{ v: "", l: "recently updated" }, { v: "recent_claims", l: "recently claimed" }]} testid="sort-coaches" />
            <button onClick={load} data-testid="admin-coach-search-btn" className="bg-volt text-white heading text-xs tracking-widest px-4 py-2 hover:glow-volt">
              {loading ? "LOADING…" : "RUN"}
            </button>
          </div>
          <div className="text-xs text-neutral-500 mt-2">{total.toLocaleString()} coaches match · showing {items.length}</div>
        </div>

        {/* Coach table */}
        <div className="mt-6 border border-neutral-800 bg-[#080A0F] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800">
              <tr>
                <th className="text-left p-3 font-normal">NAME</th>
                <th className="text-left p-3 font-normal hidden md:table-cell">ORG</th>
                <th className="text-left p-3 font-normal hidden lg:table-cell">DIV</th>
                <th className="text-left p-3 font-normal hidden lg:table-cell">CLAIMED</th>
                <th className="text-left p-3 font-normal hidden xl:table-cell">UPDATED</th>
                <th className="text-right p-3 font-normal">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-neutral-900 hover:bg-neutral-900/50" data-testid={`admin-coach-row-${c.id}`}>
                  <td className="p-3">
                    <div className="font-medium flex items-center gap-1.5">
                      {c.name || "(unnamed)"}
                      {c.email_unsubscribed && (
                        <span className="text-red-500 inline-flex" title="Unsubscribed from CoachConnect emails" data-testid={`unsubscribed-icon-${c.id}`}>
                          <MailX size={14} />
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-500">{c.headline?.slice(0, 90) || "—"}</div>
                  </td>
                  <td className="p-3 hidden md:table-cell text-neutral-300">{c.current_organization || "—"}</td>
                  <td className="p-3 hidden lg:table-cell text-neutral-400">{c.current_division || "—"}</td>
                  <td className="p-3 hidden lg:table-cell">
                    {c.claimed
                      ? <span className="text-volt text-xs" title="Coach created an account and verified ownership">ACCOUNT CREATED</span>
                      : c.claim_status === "soft_claimed"
                        ? <span className="text-sky-400 text-xs" title="Coach clicked their claim link but hasn't created an account yet">CLAIM LINK CLICKED</span>
                        : c.claim_status === "declined"
                          ? <span className="text-blaze text-xs">DECLINED</span>
                          : <span className="text-neutral-500 text-xs">unclaimed</span>}
                  </td>
                  <td className="p-3 hidden xl:table-cell text-neutral-500 text-xs">{c.updated_at?.slice(0, 10) || "—"}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/coaches/${c.id}`} className="p-1.5 hover:text-volt" title="View public profile" data-testid={`admin-coach-view-${c.id}`}>
                        <ExternalLink size={14} />
                      </Link>
                      <button onClick={() => setEditing({ ...c })} className="p-1.5 hover:text-volt" title="Edit" data-testid={`admin-coach-edit-${c.id}`}>
                        <Edit3 size={14} />
                      </button>
                      {c.email && (
                        <button
                          onClick={() => toggleOptOut(c)}
                          className={`p-1.5 ${c.email_unsubscribed ? "text-red-500 hover:text-volt" : "hover:text-red-500"}`}
                          title={c.email_unsubscribed ? "Opted out — click to re-subscribe" : "Opt out of ALL emails (STOP reply)"}
                          data-testid={`admin-coach-optout-${c.id}`}
                        >
                          <MailX size={14} />
                        </button>
                      )}
                      <button onClick={() => softDelete(c)} className="p-1.5 hover:text-blaze" title="Soft-delete" data-testid={`admin-coach-delete-${c.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!items.length && !loading && (
                <tr><td colSpan={6} className="p-8 text-center text-neutral-500">No coaches matched the filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="admin-coach-edit-modal">
          <div className="bg-[#0A101A] border border-volt max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs tracking-widest text-volt">ADMIN EDIT</div>
                <div className="heading text-2xl mt-1">{editing.name || "(unnamed)"}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-neutral-500 hover:text-white">✕</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="NAME" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="admin-edit-name" />
              <Field label="HEADLINE" value={editing.headline} onChange={(v) => setEditing({ ...editing, headline: v })} testid="admin-edit-headline" />
              <Field label="ORGANIZATION" value={editing.current_organization} onChange={(v) => setEditing({ ...editing, current_organization: v })} testid="admin-edit-org" />
              <Field label="ROLE" value={editing.current_role} onChange={(v) => setEditing({ ...editing, current_role: v })} testid="admin-edit-role" />
              <Field label="DIVISION" value={editing.current_division} onChange={(v) => setEditing({ ...editing, current_division: v })} testid="admin-edit-division" />
              <Field label="EMAIL" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testid="admin-edit-email" />
              <Field label="PHONE" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testid="admin-edit-phone" />
            </div>

            <div className="mt-6 border-t border-neutral-800 pt-5">
              <div className="text-xs tracking-widest text-volt mb-2 flex items-center gap-2"><Sparkles size={12} /> RE-ENRICH FROM BIO URL</div>
              <p className="text-xs text-neutral-500 mb-2">Paste the coach&apos;s athletic-department bio URL. Claude will rewrite their playing history, coaching stints, accolades, and CIR breakdown.</p>
              <div className="flex gap-2">
                <input
                  data-testid="admin-enrich-url"
                  value={enrichUrl}
                  onChange={(e) => setEnrichUrl(e.target.value)}
                  placeholder="https://schoolathletics.com/sports/.../coaches/firstname-lastname/123"
                  className="flex-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
                />
                <button onClick={() => enrich(editing)} disabled={enriching === editing.id} className="bg-blaze text-white heading text-xs tracking-widest px-4 py-2 hover:glow-blaze disabled:opacity-50 flex items-center gap-2" data-testid="admin-enrich-btn">
                  <RefreshCw size={12} className={enriching === editing.id ? "animate-spin" : ""} />
                  {enriching === editing.id ? "ENRICHING…" : "RE-ENRICH"}
                </button>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-5">
              <button onClick={() => hardDelete(editing)} data-testid="admin-hard-delete-btn"
                className={`text-xs tracking-widest flex items-center gap-1 px-3 py-2 transition-all ${hardConfirm ? "bg-red-600 text-white animate-pulse" : "text-red-500 hover:text-red-400"}`}>
                <Trash2 size={12} /> {hardConfirm ? "CLICK AGAIN — PERMANENT DELETE" : "HARD DELETE (PERMANENT)"}
              </button>
              <div className="flex gap-2 flex-wrap">
                <Link
                  to={`/ad/marketing/coaches/${editing.id}/edit`}
                  data-testid="admin-open-full-editor-btn"
                  className="px-4 py-2 border border-volt text-volt text-xs heading tracking-widest hover:bg-volt hover:text-white flex items-center gap-1"
                >
                  OPEN FULL EDITOR →
                </Link>
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs heading tracking-widest text-neutral-400 hover:text-white">CANCEL</button>
                <button onClick={saveEdit} className="px-4 py-2 bg-volt text-white text-xs heading tracking-widest hover:glow-volt" data-testid="admin-save-btn">SAVE</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] tracking-widest text-neutral-500">{label}</label>
      <input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
      />
    </div>
  );
}

function SelectLabeled({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] tracking-widest text-neutral-500">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="block mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none">
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-[10px] tracking-widest text-neutral-500">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="block mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none">
        {options.map((o) => (
          <option key={o} value={o}>{o || "all"}</option>
        ))}
      </select>
    </div>
  );
}
